
import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, CircularProgress, useTheme, Tooltip } from '@mui/material';
import { getStudentSignupsLast30Days, StudentSignupData } from '../../../services/dashboardService';

const StudentSignupsChart = () => {
  const [data, setData] = useState<StudentSignupData[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getStudentSignupsLast30Days();
        setData(result);
      } catch (error) {
        console.error("Failed to fetch signup data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${theme.palette.divider}` }}>
        <CircularProgress />
      </Paper>
    );
  }

  const maxCount = Math.max(...data.map(d => d.count), 1); // Avoid division by zero
  const totalSignups = data.reduce((acc, curr) => acc + curr.count, 0);

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 4, height: '100%', border: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
        Student Signups <Typography component="span" variant="body2" color="text.secondary">(Last 30 Days)</Typography>
      </Typography>

      {totalSignups === 0 ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: 0.5 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No signups in the last 30 days
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end', gap: 1, height: 300, overflowX: 'auto', pb: 1 }}>
            {data.map((item, index) => (
              <Tooltip key={index} title={`${item.date}: ${item.count} students`} arrow>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 8,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    group: 'true'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: `${(item.count / maxCount) * 100}%`,
                      bgcolor: theme.palette.primary.main,
                      borderRadius: '4px 4px 0 0',
                      opacity: 0.7,
                      transition: 'all 0.3s',
                      position: 'relative',
                      '&:hover': {
                        opacity: 1,
                        transform: 'scaleY(1.05)',
                        transformOrigin: 'bottom'
                      }
                    }}
                  />
                </Box>
              </Tooltip>
            ))}
          </Box>

          {/* X-Axis Labels (simplified) */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 1 }}>
            <Typography variant="caption" color="text.secondary">{data[0]?.date}</Typography>
            <Typography variant="caption" color="text.secondary">{data[Math.floor(data.length / 2)]?.date}</Typography>
            <Typography variant="caption" color="text.secondary">{data[data.length - 1]?.date}</Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default StudentSignupsChart;
